import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Download, Mail, Trash2, Send } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HealthCertificatesList = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Basic ${btoa(':' + password)}` };

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    fetch(`${API_URL}/api/admin/health-certificates`, { headers })
      .then((r) => r.json())
      .then((d) => setItems(d || []))
      .catch((e) => logger.error('Load HC list failed', e))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPdf = async (cert) => {
    const res = await fetch(`${API_URL}/api/admin/health-certificates/${cert.id}/pdf`, { headers });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `HealthCert_${cert.animal_name || 'Pet'}_${cert.certificate_number}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const remove = async (cert) => {
    if (!window.confirm(`Delete certificate ${cert.certificate_number}?`)) return;
    await fetch(`${API_URL}/api/admin/health-certificates/${cert.id}`, { method: 'DELETE', headers });
    setItems((arr) => arr.filter((x) => x.id !== cert.id));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Health Certificates</h1>
              <p className="text-sm text-foreground/60">Fillable PDF templates for export — issue & email to third parties</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/admin/dashboard')} data-testid="back-btn"
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <button onClick={() => navigate('/admin/health-certificates/new')} data-testid="new-hc-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Certificate
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-foreground/50">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-foreground/60">
              <FileText className="w-12 h-12 mx-auto mb-3 text-foreground/30" />
              <p>No health certificates yet. Click <strong>New Certificate</strong> to create your first one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Cert No.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Animal</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Consignor → Consignee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Export Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50" data-testid={`hc-row-${c.id}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-primary">{c.certificate_number}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-foreground">{c.animal_name || '—'}</div>
                        <div className="text-xs text-foreground/50">{c.species} {c.breed ? `· ${c.breed}` : ''}</div>
                      </td>
                      <td className="px-4 py-3 text-foreground/70">
                        <div className="text-xs">{c.consignor_name || '—'}</div>
                        <div className="text-xs">→ {c.consignee_name || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-foreground/70">{c.export_date || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded font-semibold ${c.status === 'issued' ? 'bg-emerald-200 text-emerald-900' : 'bg-gray-200 text-gray-700'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/admin/health-certificates/${c.id}/edit`)}
                            data-testid={`edit-${c.id}`}
                            title="Edit / View"
                            className="p-1.5 text-primary hover:bg-primary/10 rounded">
                            <FileText className="w-4 h-4" />
                          </button>
                          <button onClick={() => downloadPdf(c)} title="Download PDF"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => navigate(`/admin/health-certificates/${c.id}/edit?email=1`)}
                            title="Email to third party"
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Mail className="w-4 h-4" />
                          </button>
                          <button onClick={() => navigate(`/admin/email-composer?attach=health_cert:${c.id}&subject=${encodeURIComponent(`Health Certificate ${c.certificate_number || ''}`)}`)}
                            data-testid={`compose-hc-${c.id}`}
                            title="Compose an email with this certificate attached"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <Send className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(c)} title="Delete"
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthCertificatesList;
